import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {CameraImageDraw} from 'src/common/components/camera-image-draw/CameraImageDraw'
import {Pos} from 'src/common/types/generics/ImageDetections'
import {VerifyUserAndCompanyPermission} from 'src/common/utils/ValidatePermissions'
import {BtnClose} from 'src/components/BtnClose'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import {Input} from 'src/components/input/Input'
import useImage from 'src/hooks/useImage'
import {useCameraConfigContext} from 'src/pages/camera-config/context/CameraConfigContext'
import {BarrierType} from 'src/services/api'
import api from 'src/services/api/api'
import {BarrierTypeResponse, CreateBarrier} from 'src/services/api/endpoints/BarriersEndpoint'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'

type CameraModalRegisterBarrierProps = {
  open: boolean
  setOpen: (vari: boolean) => void
  editingArea?: BarrierTypeResponse | null
  setEditingArea: (edit: BarrierTypeResponse | null) => void
  snapshot: string
  refetchBarriers: () => void
}

const BarriersTypesToList = [{id: 'interest-area', label: 'Área de interesse'}]

const BarriersTypesWithLineToList = [
  {id: 'interest-area', label: 'Área de interesse'},
  {id: 'line-crossing', label: 'Cruzamento de linha'},
]

export function CameraModalRegisterBarrier({open, setOpen, editingArea, setEditingArea, snapshot, refetchBarriers}: CameraModalRegisterBarrierProps) {
  const [barrierName, setBarrierName] = useState<string>('')
  const [barrierType, setBarrierType] = useState<DropdownItem>(BarriersTypesWithLineToList.find((it) => it.id === editingArea?.type) ?? ({} as DropdownItem))

  const [saveString, setSaveString] = useState<string>()
  const [points, setPoints] = useState<Pos[]>([])

  const {setDimImage, _dimImage, camera} = useCameraConfigContext()
  const {enqueueSnackbar} = useSnackbar()
  const {width, height} = useImage(`data:image/jpeg;base64,${snapshot}`)

  useEffect(() => {
    setDimImage({height, width})

    if (editingArea) {
      const points = editingArea?.coordinates.map((point) => {
        return {
          ...point,
          x: point.x * width,
          y: point.y * height,
        }
      })
      setPoints(points)
      setBarrierName(editingArea.name)
    }
  }, [width, height])

  const validatePoints = (points: Pos[]) => {
    if (!barrierName || !barrierType.id) return false
    else if (barrierType.id === 'interest-area' && points.length <= 3) return false
    else if (barrierType.id === 'line-crossing' && (points.length <= 1 || points.length > 2)) return false
    return true
  }

  const closeModal = () => {
    setEditingArea(null)
    setSaveString(undefined)
    setOpen(false)
  }

  async function addBarrier(points: Pos[]) {
    try {
      if (!barrierName || !barrierType.id || !camera) return
      const coordinates = points.map((it) => ({
        x: Math.round(it.x) / _dimImage.width,
        y: Math.round(it.y) / _dimImage.height,
        reference: 1,
      }))

      const createBarrier = new CreateBarrier(camera.id, coordinates, barrierName, barrierType.id as BarrierType)
      await api.barriers.createBarrier(createBarrier)

      enqueueSnackbar('Barreira salva com sucesso', {
        variant: 'success',
      })
      refetchBarriers()
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao salvar a barreira')
    }
  }

  async function editBarrier(points: Pos[]) {
    if (!editingArea) return
    try {
      await api.barriers.updateBarrier({
        coordinates: points.map((it) => ({
          x: Math.round(it.x) / _dimImage.width,
          y: Math.round(it.y) / _dimImage.height,
          reference: 1,
        })),
        name: barrierName,
        id: editingArea.id,
      })
      enqueueSnackbar('Barreira atualizada com sucesso', {
        variant: 'success',
      })
      refetchBarriers()
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao atualizar barreira')
    }
  }

  const hasCreateLineCrossingPermission = VerifyUserAndCompanyPermission([camera?.companyId!], 'line-crossing:create')
  const cameraIsRtsp = camera && (camera.origin === 'RTSP' || camera.origin === 'RTMP')
  return (
    <Modal isOpen={open} closeModal={() => closeModal()} backgroundColor='white' width='800px'>
      <Box padding='20px' display='flex' direction='column' justifyContent='space-between' rowGap='30px'>
        <Box>
          <Typography variant='p' size='18px' weight={500} children='Criar nova barreira' />
          <BtnClose onClose={() => closeModal()} />
        </Box>

        <Box display='flex' direction='column' justifyContent='center' rowGap='20px'>
          <Input.Root
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='14px'
            onChange={(e) => setBarrierName(e.target.value)}
            label='Nome'
            value={barrierName}
            controlled
            min={4}
            max={100}
            maxLength={100}
          />
          <Box display='flex' height='80px'>
            <Dropdown
              labelFontSize='14px'
              borderColor='#D0D5DD'
              fontSize='14px'
              height='40px'
              label='Tipo da barreira'
              onChangeSelected={(e) => setBarrierType(e)}
              selected={barrierType}
              items={cameraIsRtsp && hasCreateLineCrossingPermission ? BarriersTypesWithLineToList : BarriersTypesToList}
              disabled={!!editingArea}
            />
          </Box>
          <Typography variant='p' size='14px' weight={500} color='rgb(54, 65, 82);'>
            Crie os pontos para formar uma {barrierType?.id === 'interest-area' ? 'área' : 'linha'}
          </Typography>
          <Box marginTop='10px' minHeight='300px'>
            <CameraImageDraw
              isKonvaImageDrawToolbar
              width={300}
              height={200}
              points={points}
              editable={true}
              maxAmountOfPoints={barrierType.id === 'line-crossing' ? 2 : undefined}
              src={`data:image/jpeg;base64,${snapshot}`}
              onSave={(points) => {
                const validatedPoints = validatePoints(points)
                if (!validatedPoints) {
                  enqueueSnackbar('A quantidade de pontos não corresponde com o tipo da barreira escolhida', {variant: 'info'})
                  setSaveString(undefined)
                  return
                }

                if (editingArea) editBarrier(points)
                else addBarrier(points)
                closeModal()
              }}
              save={saveString}
            />
          </Box>
          {barrierType.id === 'line-crossing' && (
            <Box display='flex' rowGap='8px' direction='column'>
              <b>Legenda:</b>
              <Box display='flex' align='center' columnGap='10px' justifyContent='center'>
                <Box backgroundColor='rgba(0,0,255,0.25)' width='30px' height='20px' borderRadius='3px'></Box>
                <Typography variant='p' textAlign='justify'>
                  A área azul indica a entrada do cruzamento. Quando o objeto atravessar a linha em direção ao lado azul, o sistema entende que houve uma
                  detecção.
                </Typography>
              </Box>
              <Typography variant='p'>
                <b>Dica:</b> Arraste ou inverta os pontos até que a área azul indique a direção desejada.
              </Typography>
            </Box>
          )}
        </Box>

        <Box display='flex' justify='flex-end' columnGap='10px'>
          <Button fontSize='12px' height='40px' width='150px' variant='outlined' color='#8E8E8E' onClick={() => closeModal()}>
            Cancelar
          </Button>

          <Button
            fontSize='12px'
            disabledTextColor='#8E8E8E'
            height='40px'
            width='150px'
            onClick={() => setSaveString('save')}
            disabled={!barrierName || !barrierType.id}>
            Salvar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
